/**
 * API 模式管理器
 * 控制是否使用 Mock API 還是連接真實 Database
 */

export type ApiMode = 'mock' | 'database'

const STORAGE_KEY = 'api_mode_preference'

export class ApiModeManager {
  private static instance: ApiModeManager
  private currentMode: ApiMode
  private listeners: Set<(mode: ApiMode) => void> = new Set()

  private constructor() {
    // 生產 build 且環境未允許 mock 時，忽略並清除 localStorage 覆寫——
    // 避免開發期殘留的 'mock' 偏好讓正式站整個跑假資料
    if (import.meta.env.PROD && import.meta.env.VITE_USE_MOCK !== 'true') {
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch { /* ignore */ }
      this.currentMode = 'database'
      return
    }
    // 從 localStorage 讀取用戶偏好，否則使用環境變數判斷
    const storedMode = this.getStoredMode()
    this.currentMode = storedMode || this.getDefaultMode()
  }

  static getInstance(): ApiModeManager {
    if (!ApiModeManager.instance) {
      ApiModeManager.instance = new ApiModeManager()
    }
    return ApiModeManager.instance
  }

  /**
   * 獲取預設模式（基於環境變數）
   */
  private getDefaultMode(): ApiMode {
    // 檢查環境變數
    const envUseMock = import.meta.env.VITE_USE_MOCK === 'true'
    const apiUrl = import.meta.env.VITE_API_URL || ''
    const isLocalhost = apiUrl.includes('localhost')
    
    // 如果明確設置為 true 或者是 localhost，預設使用 mock
    if (envUseMock || isLocalhost) {
      return 'mock'
    }
    
    return 'database'
  }

  /**
   * 從 localStorage 獲取存儲的模式
   */
  private getStoredMode(): ApiMode | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored && (stored === 'mock' || stored === 'database')) {
        return stored as ApiMode
      }
    } catch (error) {
      console.warn('Failed to read API mode from localStorage:', error)
    }
    return null
  }

  /**
   * 保存模式到 localStorage
   */
  private saveMode(mode: ApiMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch (error) {
      console.warn('Failed to save API mode to localStorage:', error)
    }
  }

  /**
   * 獲取當前模式
   */
  getCurrentMode(): ApiMode {
    return this.currentMode
  }

  /**
   * 設置模式
   */
  setMode(mode: ApiMode): void {
    if (this.currentMode !== mode) {
      this.currentMode = mode
      this.saveMode(mode)

      console.log(`API Mode switched to: ${mode.toUpperCase()}`)

      // 通知所有監聽器
      this.listeners.forEach(listener => {
        try {
          listener(mode)
        } catch (error) {
          console.error('Error in API mode listener:', error)
        }
      })

      // 兩個資料來源的快取與登入憑證互不相通：
      // 整頁重載，避免 React Query 快取與 JWT 殘留造成半真半假的畫面
      if (typeof window !== 'undefined') {
        window.location.reload()
      }
    }
  }

  /**
   * 切換模式
   */
  toggleMode(): void {
    const newMode = this.currentMode === 'mock' ? 'database' : 'mock'
    this.setMode(newMode)
  }

  /**
   * 檢查是否使用 Mock API
   */
  shouldUseMock(): boolean {
    return this.currentMode === 'mock'
  }

  /**
   * 添加模式變更監聽器
   */
  addListener(listener: (mode: ApiMode) => void): () => void {
    this.listeners.add(listener)
    
    // 返回移除監聽器的函數
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * 獲取當前 API 配置信息
   */
  getApiInfo() {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
    const envUseMock = import.meta.env.VITE_USE_MOCK === 'true'
    
    return {
      currentMode: this.currentMode,
      apiUrl,
      envUseMock,
      isOverride: this.getStoredMode() !== null,
      defaultMode: this.getDefaultMode()
    }
  }

  /**
   * 重置為環境預設模式
   */
  resetToDefault(): void {
    const defaultMode = this.getDefaultMode()
    localStorage.removeItem(STORAGE_KEY)
    this.setMode(defaultMode)
  }
}

// 導出單例實例
export const apiModeManager = ApiModeManager.getInstance()

// 向後兼容的函數
export function shouldUseMock(): boolean {
  return apiModeManager.shouldUseMock()
}